import {
  Typography,
  Grid,
  TextField,
  InputAdornment,
  Box,
  Link,
  Tooltip,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { Formik, ErrorMessage, FormikProps, Form } from "formik";
import {
  Feature,
  PricingType,
  ProductType,
  SetupType,
} from "../../models/features/Integration/DmsSetup";
import { SetupProfile } from "../../models/features/SetupProfile/SetupProfile";
import { useDispatch, useSelector } from "react-redux";
import { Dispatch } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import React from "react";
import { useGetDmsSetupByIdMutation } from "../../redux/reducer/features/Integration/DmsSetupSlice";
import LinkIcon from "@mui/icons-material/Link";
import KeyIcon from "@mui/icons-material/Key";
import InfoIcon from "@mui/icons-material/Info";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { AppState } from "../../redux/reducer/rootReducer";
import {
  setupTypeLabelMap,
  usePriceOptions,
} from "../../services/utilities/utility";
import PageLoader from "../app/PageLoader";
import GridWithNewPrices from "../Integration/Synchronization/Grids/DefaultPriceSetting";
import { hasFeatureAccess } from "../../services/featurePermissions/featureMatrix";

export const SiteDetails: React.FunctionComponent = () => {
  const priceOptions = usePriceOptions();
  const formRef: React.Ref<FormikProps<SetupProfile>> = React.useRef(null);

  const dmsSetup = useSelector((state: AppState) => state.authReducer.dmsSetup);
  const setupId = useSelector((state: AppState) => state.authReducer.setupId);

  const [setupProfileData, setSetupProfileData] = useState<SetupProfile>(
    {} as SetupProfile,
  );
  const [openPriceType, setOpenPriceType] = useState(false);
  const [productType, setProductType] = useState(ProductType.Wholegoods);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);

  const [dmsSetupMutation, dmsSetupMutationResult] =
    useGetDmsSetupByIdMutation();

  useEffect(() => {
    dmsSetupMutation({}).then((response: any) => {
      if (response && response.data && response.data.status === 0) {
        let data = response?.data.entity;
        setSetupProfileData({
          id: data.id,
          storeUrl: data.storeUrl,
          storeAccessToken: data.storeAccessToken,
          siteName: data.siteName,
          siteDomain: data.siteDomain,
          IsUpdateCustomerAndPaymentOnly: false,
        });
      }
    });
  }, []);

  const handleClose = () => {
    setOpenPriceType(false);
  };

  const handleCopySetupId = () => {
    if (setupId) {
      navigator.clipboard.writeText(setupId);
      setCopySnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setCopySnackbarOpen(false);
  };
  return (
    <>
      <PageLoader isLoading={dmsSetupMutationResult.isLoading}></PageLoader>
      <Box sx={{ pb: 2, mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold" }} gutterBottom>
          Site details
        </Typography>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <TextField
            id="siteName"
            name="siteName"
            label="Site Name"
            fullWidth
            variant="standard"
            value={setupProfileData.siteName}
            InputProps={{
              readOnly: true,
            }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            id="siteDomain"
            name="siteDomain"
            label="Site Domain"
            fullWidth
            value={setupProfileData.siteDomain}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <LinkIcon sx={{ color: "rgb(56, 55, 110)" }} />
                </InputAdornment>
              ),
            }}
            variant="standard"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        {hasFeatureAccess(dmsSetup?.setupType, Feature.SetupIdSharing) && (
          <Grid item xs={6}>
            <TextField
              id="dealerSetupId"
              name="dealerSetupId"
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Dealer Setup ID
                  <Tooltip
                    title={`Copy and share this ID with ${setupTypeLabelMap[dmsSetup?.setupType ?? SetupType.HiImpact]} for configuration of Data endpoints. This is confidential information, please do not share it with unauthorized parties.`}
                    arrow
                  >
                    <InfoIcon />
                  </Tooltip>
                </Box>
              }
              fullWidth
              variant="standard"
              value={setupId || ""}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Copy Setup ID">
                      <IconButton
                        onClick={handleCopySetupId}
                        edge="end"
                        sx={{ color: "rgb(56, 55, 110)" }}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        )}
      </Grid>
      <Box sx={{ pb: 2, mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold" }} gutterBottom>
          Pricing configuration
        </Typography>
      </Box>
      <Box>
        {dmsSetup?.productTypes?.some((type) => type === "Wholegoods") && (
          <Grid item xs={12} display="flex" alignItems="center" gap={1}>
            <Typography
              sx={{ fontWeight: "bold", pt: "5px" }}
              variant="subtitle2"
              gutterBottom
            >
              Wholegoods default price level:
            </Typography>
            {priceOptions
              .filter(
                (x: any) =>
                  x.type === PricingType[dmsSetup?.priceType ?? "Price"],
              )
              .map((option) => (
                <Typography fontSize="0.9rem">{option.label}</Typography>
              ))}
            <Link
              sx={{
                cursor: "pointer",
                fontStyle: "italic",
              }}
              onClick={() => {
                setProductType(ProductType.Wholegoods);
                setOpenPriceType(true);
              }}
            >
              Change
            </Link>
          </Grid>
        )}
        {dmsSetup?.productTypes?.some((type) => type === "Parts") && (
          <Grid item xs={12} display="flex" alignItems="center" gap={1}>
            <Typography
              sx={{ fontWeight: "bold", pt: "5px" }}
              variant="subtitle2"
              gutterBottom
            >
              Parts default price level:
            </Typography>
            {priceOptions
              .filter(
                (x: any) =>
                  x.type === PricingType[dmsSetup?.partsPriceType ?? "Price"],
              )
              .map((option) => (
                <Typography fontSize="0.9rem">{option.label}</Typography>
              ))}
            <Link
              sx={{
                cursor: "pointer",
                fontStyle: "italic",
              }}
              onClick={() => {
                setProductType(ProductType.Parts);
                setOpenPriceType(true);
              }}
            >
              Change
            </Link>
          </Grid>
        )}
      </Box>
      {openPriceType ? (
        <GridWithNewPrices
          handleClose={handleClose}
          isOpen={openPriceType}
          productType={productType}
        />
      ) : (
        <></>
      )}
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: "100%" }}
        >
          Setup ID copied to clipboard!
        </Alert>
      </Snackbar>{" "}
    </>
  );
};
